import { useMemo, useState } from "react";
import { differenceInDays, isAfter } from "date-fns";

import {
  Box,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDDialog from "components/MDDialog";

import CardListLotes from "./CardLotes";
import ModalAddLote from "./ModalAddLote";
import {
  LOTE_ACCENT,
  LOTE_ACCENT_SOFT,
  loteToolbarSx,
} from "./lotes-evento.theme";

import { setMessage, useMaterialUIController } from "context";

import { ILotes } from "interfaces/lotes";
import { IEventoResponse } from "interfaces/eventos";
import { deleteLote } from "services/lotes";
import { formatDateEndOfDay } from "helpers/formatDate";

interface Props {
  evento: IEventoResponse;
  updateEvento: () => void;
}

function StatCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent?: boolean;
}) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: accent ? "rgba(249, 96, 48, 0.3)" : "grey.200",
        bgcolor: accent ? "rgba(249, 96, 48, 0.04)" : "background.paper",
        height: "100%",
      }}
    >
      <Typography
        variant="h5"
        fontWeight="bold"
        color={accent ? LOTE_ACCENT : "text.primary"}
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

export default function LotesEvento({
  evento,
  updateEvento,
}: Props): JSX.Element {
  const [, dispatch] = useMaterialUIController();

  const [open, setOpen] = useState(false);
  const [loteSelected, setLoteSelected] = useState<ILotes | null>(null);
  const [isEdit, setIsEdit] = useState(false);
  const [openDialogDelete, setOpenDialogDelete] = useState(false);
  const [loteToDelete, setLoteToDelete] = useState<ILotes | null>(null);

  const lotes = evento?.lotesEventos || [];

  const inicioInscricao = formatDateEndOfDay(evento?.inicioIncricao);
  const finalInscricao = formatDateEndOfDay(evento?.fimInscricao);

  const atingiuDataLimite = useMemo(() => {
    const dataUltimoLote =
      lotes[lotes.length - 1]?.dataFinal
        .slice(0, 10)
        .concat(`T23:59:59.000Z`) || new Date();
    const fimInscricao = evento.fimInscricao
      .slice(0, 10)
      .concat(`T23:59:59.000Z`);
    return isAfter(fimInscricao, dataUltimoLote);
  }, [lotes, evento.fimInscricao]);

  const stats = useMemo(() => {
    const totalTags = lotes.reduce(
      (sum, lote) => sum + (lote.valoresTags?.length || 0),
      0
    );
    const totalDias = lotes.reduce((sum, lote) => {
      return (
        sum +
        Math.max(
          0,
          differenceInDays(new Date(lote.dataFinal), new Date(lote.dataInicio))
        )
      );
    }, 0);
    return {
      total: lotes.length,
      totalTags,
      totalDias,
    };
  }, [lotes]);

  const handleOpenModal = () => setOpen((prev) => !prev);

  const handleNewLote = () => {
    setIsEdit(false);
    setLoteSelected(null);
    setOpen(true);
  };

  const handleSelectedLoteEdit = (lote: ILotes) => {
    setIsEdit(true);
    setLoteSelected(lote);
    setOpen(true);
  };

  const handleAskRemoveLote = (lote: ILotes) => {
    setLoteToDelete(lote);
    setOpenDialogDelete(true);
  };

  const handleRemoveLote = async () => {
    if (!loteToDelete) return;
    try {
      await deleteLote(loteToDelete.id);
      updateEvento();
      setOpenDialogDelete(false);
      setLoteToDelete(null);
    } catch (erro: any) {
      setMessage(dispatch, {
        open: true,
        text: erro.message,
        severity: "error",
      });
    }
  };

  return (
    <MDBox id="lotes">
      <ModalAddLote
        isEdit={isEdit}
        evento={evento}
        loteSelected={loteSelected}
        updateEvento={updateEvento}
        handleSetLoteSelected={setLoteSelected}
        open={open}
        handleClose={handleOpenModal}
      />

      <MDDialog
        title="Excluir lote"
        message={`Deseja excluir o lote selecionado? As tags vinculadas a ele também serão removidas.`}
        open={openDialogDelete}
        onClose={() => {
          setOpenDialogDelete(false);
          setLoteToDelete(null);
        }}
        response={(resp: boolean) => {
          if (resp) void handleRemoveLote();
          else setOpenDialogDelete(false);
        }}
      />

      <Box sx={loteToolbarSx}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <EventNoteOutlinedIcon sx={{ color: LOTE_ACCENT }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                Lotes de inscrição
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {evento.titulo} · Período {inicioInscricao} a {finalInscricao}
              </Typography>
            </Box>
          </Stack>
          {atingiuDataLimite && (
            <MDButton
              variant="gradient"
              color="dark"
              onClick={handleNewLote}
              startIcon={<AddOutlinedIcon />}
              sx={{
                flexShrink: 0,
                background: `linear-gradient(195deg, ${LOTE_ACCENT}, #e04a1a)`,
              }}
            >
              Novo lote
            </MDButton>
          )}
        </Stack>
      </Box>

      <Grid container spacing={2} mb={2.5}>
        <Grid item xs={6} sm={4}>
          <StatCard label="Total de lotes" value={stats.total} accent />
        </Grid>
        <Grid item xs={6} sm={4}>
          <StatCard label="Tags nos lotes" value={stats.totalTags} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard label="Dias cobertos" value={stats.totalDias} />
        </Grid>
      </Grid>

      {lotes.length === 0 ? (
        <Box
          sx={{
            py: 6,
            px: 2,
            textAlign: "center",
            borderRadius: 2,
            border: "1px dashed",
            borderColor: "grey.300",
            bgcolor: "grey.50",
          }}
        >
          <EventNoteOutlinedIcon
            sx={{ fontSize: 44, color: "grey.400", mb: 1 }}
          />
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Nenhum lote cadastrado
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Defina os períodos e valores de inscrição deste evento.
          </Typography>
          {atingiuDataLimite && (
            <MDButton
              variant="outlined"
              color="dark"
              onClick={handleNewLote}
              startIcon={<AddOutlinedIcon />}
            >
              Adicionar primeiro lote
            </MDButton>
          )}
        </Box>
      ) : (
        <Stack spacing={1.5}>
          {lotes.map((lote, idx) => (
            <CardListLotes
              orderLote={idx + 1}
              selectedLote={handleSelectedLoteEdit}
              removeLote={handleAskRemoveLote}
              lote={lote}
              key={lote.id}
            />
          ))}

          {atingiuDataLimite && (
            <Box
              onClick={handleNewLote}
              sx={{
                py: 2,
                px: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                borderRadius: 2,
                border: "1px dashed",
                borderColor: "grey.300",
                bgcolor: "grey.50",
                cursor: "pointer",
                transition: "background-color 0.15s, border-color 0.15s",
                "&:hover": {
                  bgcolor: LOTE_ACCENT_SOFT,
                  borderColor: "rgba(249, 96, 48, 0.45)",
                },
              }}
            >
              <AddOutlinedIcon sx={{ color: LOTE_ACCENT, fontSize: 20 }} />
              <Typography variant="button" fontWeight={600} color="text.secondary">
                Adicionar próximo lote
              </Typography>
            </Box>
          )}
        </Stack>
      )}
    </MDBox>
  );
}